import { lazy, Suspense, useEffect, useState } from "react"

import { shallowEqualObjects } from "@/helpers/utils"
import { usePrevious } from "@/hooks/usePrevious"
import useServices from "@/hooks/useServices"
import { useTranslation } from "@/hooks/useTranslation"
import { ExportButton } from "@/molecules/ExportButton"
import { Filter } from "@/organisms/FilterNew/Filter"
import { KodemScoreFilter } from "@/organisms/FilterNew/KodemScoreFilter"
import { InputSmartSearchLoading, SmartSearch } from "@/organisms/SmartSearch"
import { useFiltersTriage } from "@/pages/TriagePage/application/useTriage"
import { ActivityGroupIssueStatus } from "./ActivityGroupIssueStatus"

import type { Option } from "@/organisms/SmartSearch/types/options.types"
import type { FilterTriageTabProps } from "./FilterTriageTab.types"

const DemoExploitabilityFilter = lazy(() =>
  import("./DemoExploitabilityFilter").then((m) => ({
    default: m.DemoExploitabilityFilter
  }))
)

export const FilterTriageTab = (props: FilterTriageTabProps) => {
  const [{ response }, isLoading] = useFiltersTriage()
  const [key, setKey] = useState<number>(Date.now())
  const { isDataAvailable, searchedVulnerabilities } = props
  const disableExport = !isDataAvailable || isLoading
  const { t } = useTranslation()
  const { auth } = useServices()
  const exploitabilityEnabled = auth.isDemo()

  const {
    issueType = [],
    issueStatus = [],
    allFilters = [],
    exploitMaturity = [],
    severity = [],
    label = [],
    osLanguages = []
  } = response || ({} as Record<string, Option[]>)
  const prevLabels = usePrevious(label)

  useEffect(() => {
    if (
      prevLabels?.length === label.length &&
      prevLabels.every((value, index) =>
        shallowEqualObjects(value, label[index])
      )
    ) {
      return
    }

    setKey(Date.now())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [label])

  const placement = "triage-page"

  return (
    <Filter.Provider isLoading={isLoading}>
      <Filter.Template
        isLoading={isLoading}
        aside={<ExportButton isLoading={disableExport} exportType="Triage" />}
        search={
          isLoading ? (
            <InputSmartSearchLoading />
          ) : (
            <SmartSearch
              id="FilterTriage"
              key={key}
              filters={allFilters}
              searchedVulnerabilities={searchedVulnerabilities}
            />
          )
        }
      >
        <Filter.Checkbox
          keyFilter="issueType"
          items={issueType}
          placeHolderDefaultValue="Issue Type"
          isLoading={isLoading}
          placement={placement}
        />
        <Filter.Checkbox
          keyFilter="issueStatus"
          order="none"
          items={issueStatus}
          placeHolderDefaultValue="Issue Status"
          isLoading={isLoading}
          placement={placement}
          t={t}
          getGroups={ActivityGroupIssueStatus}
        />
        <Filter.Insights
          filters={allFilters}
          isLoading={isLoading}
          placement={placement}
        />
        {exploitabilityEnabled ? (
          <Suspense fallback={null}>
            <DemoExploitabilityFilter
              isLoading={isLoading}
              exploitMaturity={exploitMaturity}
              placement={placement}
            />
          </Suspense>
        ) : (
          <Filter.Checkbox
            isLoading={isLoading}
            keyFilter="exploitMaturity"
            items={exploitMaturity}
            placeHolderDefaultValue="Exploitability"
            placement={placement}
          />
        )}
        <KodemScoreFilter
          isLoading={isLoading}
          maxValue={1000}
          placeHolderDefaultValue="Score"
          prefix={`${t("general.kodemScore.plain")} ≥`}
          label={t("general.kodemScore.plain")}
          placement={placement}
        />
        <Filter.Checkbox
          isLoading={isLoading}
          order="none"
          keyFilter="severity"
          items={severity}
          placeHolderDefaultValue="Severity"
          placement={placement}
        />
        <Filter.FixInsights
          filters={allFilters}
          isLoading={isLoading}
          placement={placement}
        />
        <Filter.OsLanguage
          t={t}
          isLoading={isLoading}
          options={osLanguages}
          placement={placement}
        />
      </Filter.Template>
    </Filter.Provider>
  )
}
